import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Tab,
  Nav,
  ProgressBar,
  Alert,
  Modal,
} from "react-bootstrap";
import {
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaLayerGroup,
  FaBook,
  FaUsers,
  FaCertificate,
  FaFolder,
  FaCalendar,
  FaUserGraduate,
  FaClipboardList,
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../../components/common/Header";
import BatchService from "../../../services/batchService";
import BatchStudentsTab from "./components/BatchStudentsTab";
import BatchMaterialsTab from "./components/BatchMaterialsTab";
import BatchCertificatesTab from "./components/BatchCertificatesTab";
import BatchCourseMaterialsTab from "./components/BatchCourseMaterialsTab";
import BatchForm from "./BatchForm";
import AssignmentsTab from "./components/AssignmentsTab";

const BatchDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [students, setStudents] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    loadBatchData();
  }, [id]);

  const loadBatchData = async () => {
    try {
      setLoading(true);
      const response = await BatchService.getBatchById(id);
      setBatch(response.data.batch);
      setStudents(response.data.students || []);
      setAvailableStudents(response.data.available_students || []);
    } catch (error) {
      toast.error("Failed to load batch details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await BatchService.deleteBatch(id);
      toast.success("Batch deleted successfully");
      navigate("/admin/batches");
    } catch (error) {
      toast.error("Failed to delete batch");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge bg="success">Active</Badge>;
      case "upcoming":
        return <Badge bg="info">Upcoming</Badge>;
      case "completed":
        return <Badge bg="secondary">Completed</Badge>;
      default:
        return <Badge bg="warning">{status}</Badge>;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const completedStudents = students.filter(
    (s) => s.status === "completed",
  ).length;
  const activeStudents = students.filter((s) => s.status === "active").length;
  const progress =
    students.length > 0
      ? Math.round((completedStudents / students.length) * 100)
      : 0;

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container-fluid p-4 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div>
        <Header />
        <div className="container-fluid p-4 text-center">
          <h3>Batch not found</h3>
          <Button variant="primary" onClick={() => navigate("/admin/batches")}>
            Back to Batches
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container-fluid p-4">
        {/* Back Button */}
        <Button
          variant="link"
          className="mb-3 text-decoration-none"
          onClick={() => navigate("/admin/batches")}
        >
          <FaArrowLeft className="me-2" /> Back to Batches
        </Button>

        {/* Batch Header Card */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-circle me-3">
                    <FaLayerGroup size={30} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="mb-1">{batch.name}</h2>
                    <p className="text-muted mb-0">
                      <FaBook className="me-1" /> {batch.course_name}
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-md-end">
                {getStatusBadge(batch.status)}
                <Button
                  variant="warning"
                  size="sm"
                  className="ms-2"
                  onClick={() => setShowEditModal(true)}
                >
                  <FaEdit className="me-1" /> Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="ms-2"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <FaTrash className="me-1" /> Delete
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Batch Info Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-info text-white">
              <Card.Body>
                <h6>Duration</h6>
                <h5>
                  <FaCalendar className="me-2" />
                  {formatDate(batch.start_date)} - {formatDate(batch.end_date)}
                </h5>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-success text-white">
              <Card.Body>
                <h6>Total Students</h6>
                <h2>{students.length}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-primary text-white">
              <Card.Body>
                <h6>Completed</h6>
                <h2>{completedStudents}</h2>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="shadow-sm border-0 bg-warning text-white">
              <Card.Body>
                <h6>Batch Progress</h6>
                <h2>{progress}%</h2>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Progress Bar */}
        <Card className="mb-4 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between mb-2">
              <span>Overall Batch Completion</span>
              <span className="fw-bold">{progress}%</span>
            </div>
            <ProgressBar now={progress} variant="success" />
          </Card.Body>
        </Card>

        {/* Tabs */}
        <Card className="shadow-sm">
          <Card.Header>
            <Nav
              variant="tabs"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
            >
              <Nav.Item>
                <Nav.Link eventKey="overview">
                  <FaBook className="me-2" /> Overview
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="students">
                  <FaUsers className="me-2" /> Students ({students.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="materials">
                  <FaFolder className="me-2" /> Batch Materials
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="course-materials">
                  <FaBook className="me-2" /> Course Materials Control
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="certificates">
                  <FaCertificate className="me-2" /> Certificates
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="assignments">
                  <FaClipboardList className="me-2" /> Assignments
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>

          <Card.Body>
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div>
                <h5>Batch Information</h5>
                <Row className="mt-4">
                  <Col md={6}>
                    <Card className="bg-light">
                      <Card.Body>
                        <h6>Course Details</h6>
                        <hr />
                        <p>
                          <strong>Course:</strong> {batch.course_name}
                        </p>
                        <p>
                          <strong>Duration:</strong> {batch.course_duration}
                        </p>
                        <p>
                          <strong>Price:</strong> ₹{batch.course_price}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="bg-light">
                      <Card.Body>
                        <h6>Batch Statistics</h6>
                        <hr />
                        <p>
                          <strong>Total Students:</strong> {students.length}
                        </p>
                        <p>
                          <strong>Active:</strong> {activeStudents}
                        </p>
                        <p>
                          <strong>Completed:</strong> {completedStudents}
                        </p>
                        <p>
                          <strong>Progress:</strong> {progress}%
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            )}

            {/* Students Tab */}
            {activeTab === "students" && (
              <BatchStudentsTab
                batchId={id}
                students={students}
                availableStudents={availableStudents}
                onStudentAdded={loadBatchData}
                onStudentRemoved={loadBatchData}
                onProgressUpdated={loadBatchData}
              />
            )}

            {/* Batch Materials Tab */}
            {activeTab === "materials" && <BatchMaterialsTab batchId={id} />}

            {/* Course Materials Control Tab */}
            {activeTab === "course-materials" && (
              <BatchCourseMaterialsTab
                batchId={id}
                courseName={batch.course_name}
              />
            )}

            {/* Certificates Tab */}
            {activeTab === "certificates" && (
              <BatchCertificatesTab
                batchId={id}
                batchName={batch.name}
                students={students}
                onCertificateIssued={loadBatchData}
              />
            )}
            {/* Assignments Tab */}
            {activeTab === "assignments" && <AssignmentsTab batchId={id} />}
          </Card.Body>
        </Card>

        {/* Edit Modal */}
        <BatchForm
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          batch={batch}
          onSuccess={() => {
            setShowEditModal(false);
            loadBatchData();
          }}
        />

        {/* Delete Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">
              <FaTrash className="me-2" />
              Confirm Delete
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Are you sure you want to delete this batch?</p>
            <p className="fw-bold text-danger">{batch?.name}</p>
            <Alert variant="warning">
              This will also remove all students and materials from this batch.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete Batch
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default BatchDetails;
